import { useTicketInformation } from '@/hooks/useTicketInformation/useTicketInformation';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
import { getFormattedDateTime } from '@eventbrite/legacy-infra.datetime-fns';
import { CONVERSION_BAR_MESSAGES } from '../../constants';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { getDisplayTimezone } from '@/utils/dateTime';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';

import { CheckoutButton } from '../../CheckoutButton/CheckoutButton';
import styles from './SalesStartSoonPanel.module.scss';
import { RemindMe } from './RemindMe/RemindMe';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { shouldShowRemindMe } from './shouldShowRemindMe';
import { useEarlyAccessState } from './useEarlyAccessState';

const EARLY_ACCESS_ANNOUNCEMENT = defineMessage({
  id: 'Your early access sale starts {date}',
  defaultMessage: 'Your early access sale starts {date}',
  description: 'Eventbrite+ Early Access announcement in the conversion bar; {date} renders on its own line',
});

// e.g. "Oct 1, 2026, 10:00 AM" — mirrors the Figma announcement copy.
const EARLY_ACCESS_DATE_FORMAT = 'MMM d, yyyy, h:mm a';

// Some locales lowercase the month abbreviation ("set 19, 2026"); the design uses an initial cap.
const capitalizeFirstLetter = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const SalesStartSoonPanel: React.FC = () => {
  const { locale } = useIntl();
  const { isOnline, isListed, isProtected } = useBasicInfo();
  const attendeeTimezone = useAttendeeTimezone(isOnline ?? false);
  const [data, loading] = useTicketInformation();
  const { phase: earlyAccessPhase, windowOpenIso } = useEarlyAccessState();
  const showEarlyAccessAnnouncement = earlyAccessPhase === 'announcement';

  if (loading) {
    return (
      <div className={styles.detailsLoadingSkeleton}>
        <LoadingSkeleton height={30} width="100%" />
        <LoadingSkeleton height={30} width="100%" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const timezone = getDisplayTimezone({
    isOnlineEvent: isOnline ?? false,
    eventTimezone: data.startSalesDate?.timezone,
    attendeeTimezone,
  });
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showTimezone = timezone !== browserTimezone;
  const startDate = getFormattedDateTime(data.startSalesDate?.utc, showTimezone, timezone, locale, 'PPp');
  const showRemindMe = shouldShowRemindMe({
    isListed: isListed ?? false,
    isProtected: isProtected ?? false,
    startSalesDateUtc: data.startSalesDate?.utc,
  });
  const earlyAccessStartDate =
    showEarlyAccessAnnouncement && windowOpenIso
      ? capitalizeFirstLetter(
          getFormattedDateTime(windowOpenIso, showTimezone, timezone, locale, EARLY_ACCESS_DATE_FORMAT),
        )
      : '';

  return (
    <div className={styles.container} data-testid="sales-start-soon-panel">
      <div className={showEarlyAccessAnnouncement ? `${styles.content} ${styles.earlyAccessContent}` : styles.content}>
        <div
          className={showEarlyAccessAnnouncement ? `${styles.label} ${styles.earlyAccessLabel}` : styles.label}
          data-testid="sales-start-soon-panel-label"
        >
          {showEarlyAccessAnnouncement ? (
            <FormattedMessage
              {...EARLY_ACCESS_ANNOUNCEMENT}
              values={{
                date: <span className={styles.earlyAccessDate}>{earlyAccessStartDate}</span>,
              }}
            />
          ) : (
            <FormattedMessage {...CONVERSION_BAR_MESSAGES.SALES_START_SOON} values={{ date: startDate }} />
          )}
        </div>
      </div>
      {showRemindMe ? (
        <RemindMe secondaryAction={<CheckoutButton isRemindMe />} />
      ) : (
        <div data-testid="cb-cta-area" className={styles.ctaButtonWrapper}>
          <CheckoutButton isRemindMe />
        </div>
      )}
    </div>
  );
};
